import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const TeamMember = ({ name, role, image }) => (
    <motion.div
        className="flex flex-col items-center"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
    >
        <img
            src={image}
            alt={name}
            className="w-full aspect-square mb-4 object-cover shadow-lg transition-all duration-300"
        />
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
        >
            <h3 className="text-xl font-bold">{name}</h3>
            <p className="text-gray-500">{role}</p>
        </motion.div>
    </motion.div>
);

const TimelineEvent = ({ year, title, description, progress, isActive }) => {
    return (
        <div className="relative flex items-center my-12">
            {/* Vertical oval shape */}
            <motion.div
                className={`w-16 h-32 rounded-full mr-6 transition-colors duration-500 ${isActive
                    ? 'bg-gradient-to-b from-cocoa-500 to-cream-500'
                    : 'bg-gray-300'
                    }`}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.5 }}
            />

            {/* Content */}
            <motion.div
                className={`p-6 rounded-lg shadow-md flex-1 transition-all duration-500 ${isActive
                    ? 'opacity-100 translate-x-0 bg-white'
                    : 'opacity-30 translate-x-10 bg-gray-100'
                    }`}
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
            >
                <h3 className="text-2xl font-bold">{year}</h3>
                <h4 className="text-xl font-bold mt-2">{title}</h4>
                <p className="text-gray-500 mt-2">{description}</p>
            </motion.div>
        </div>
    );
};

const HistoryTimeline = () => {
    const { scrollYProgress } = useScroll();
    const progressBarWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

    // Mise à jour des événements avec l'histoire réelle d'ISILIVING
    const timelineEvents = [
        {
            year: 2016,
            title: "Création d'ISILIVING",
            description: "Fondation d'ISILIVING à Aix-en-Provence par Jacqueline Taioglou, avec une vision d'excellence dans le mobilier design et l'aménagement intérieur.",
            image: "https://images.pexels.com/photos/4642438/pexels-photo-4642438.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
        },
        {
            year: 2018,
            title: "Création du Bureau d'Études",
            description: "Extension des services avec la création d'un bureau d'études dédié aux projets d'aménagement pour particuliers et professionnels.",
            image: "https://images.pexels.com/photos/5905435/pexels-photo-5905435.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        },
        {
            year: 2020,
            title: "Partenariats Prestigieux",
            description: "Établissement de partenariats avec des marques de renom comme Herman Miller, Vitra, et USM, renforçant notre position dans le mobilier haut de gamme.",
            image: "https://images.pexels.com/photos/53464/sheraton-palace-hotel-lobby-architecture-san-francisco-53464.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        },
        {
            year: 2023,
            title: "Innovation Continue",
            description: "Lancement de nouveaux services d'aménagement et expansion de notre showroom pour mieux servir notre clientèle.",
            image: "https://images.pexels.com/photos/7552359/pexels-photo-7552359.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        }
    ];

    return (
        <div className="relative py-12 px-8 bg-cream">
            {/* Ligne de progression */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-cocoa/20">
                <motion.div
                    className="absolute top-0 w-full bg-cocoa"
                    style={{ height: progressBarWidth }}
                />
            </div>

            {/* Timeline Events */}
            <div className="space-y-24">
                {timelineEvents.map((event, index) => (
                    <motion.div
                        key={event.year}
                        className="flex gap-8 items-start"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                    >
                        <div className="flex-shrink-0 w-32 text-right">
                            <span className="text-4xl font-cormorant text-cocoa">{event.year}</span>
                        </div>

                        <div className="flex-grow bg-white p-6 rounded-lg shadow-lg">
                            <h3 className="text-2xl font-cormorant text-cocoa mb-4">{event.title}</h3>
                            <p className="text-gray-600 mb-4">{event.description}</p>
                            {event.image && (
                                <img
                                    src={event.image}
                                    alt={event.title}
                                    className="w-full h-48 object-cover rounded-lg"
                                />
                            )}
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

const OurHistoryPage = () => {
    const teamMembers = [
        {
            name: "Jacquie Taioglou",
            role: "Directrice Générale",
            image: "/assets/images/histoire/jacquie-taioglou.jpeg"
        },
    ];

    return (
        <div className="bg-cream">
            {/* Hero Section - Maintenant responsive */}
            <div className="relative h-[50vh] md:h-[70vh] lg:h-screen overflow-hidden">
                <motion.div
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage: "url('/assets/images/showroom-history.jpeg')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'center top',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: '#1A1A1A',
                    }}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5 }}
                >
                    <div className="absolute inset-0 bg-black/50" />
                </motion.div>

                <div className="relative z-10 h-full flex flex-col items-center justify-center text-cream px-4">
                    <motion.h1
                        className="text-4xl md:text-6xl lg:text-8xl font-cormorant font-bold mb-4 md:mb-6 text-center"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        Notre Histoire
                    </motion.h1>
                    <motion.p
                        className="text-base md:text-xl max-w-2xl text-center px-4"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        Depuis 2016, ISILIVING façonne des espaces d'exception,
                        alliant design contemporain et excellence française.
                    </motion.p>
                </div>
            </div>

            <div className="container mx-auto py-12 px-4">
                {/* Section Histoire */}
                <div className="max-w-4xl mx-auto mb-16">
                    <h1 className="text-4xl md:text-6xl font-cormorant text-center mb-8">Notre Histoire</h1>
                    <p className="text-lg text-gray-700 mb-8">
                        ISILIVING – Mobilier Contemporain & Aménagement sur Mesure à Aix-en-Provence
                    </p>
                    <p className="text-lg text-gray-700">

                        Bienvenue chez ISILIVING,
                        votre spécialiste en mobilier contemporain et en aménagement d’espaces pour particuliers et professionnels à Aix-en-Provence.
                    </p>
                    <p className="text-lg text-gray-700">
                        Fondé en 2016 par Jacquie Taioglou, ISILIVING est né d’une passion pour le design et l’harmonie des espaces.
                    </p>
                    <p className="text-lg text-gray-700">
                        Avec une sélection de pièces élégantes et des solutions d’aménagement sur-mesure, nous créons des intérieurs alliant esthétique, confort et fonctionnalité.
                    </p>
                    <p className="text-lg text-gray-700">

                        Que vous souhaitiez réinventer votre espace de vie ou optimiser un environnement de travail, notre expertise vous accompagne à chaque étape.

                        Laissez-vous inspirer par nos collections et nos conseils personnalisés pour donner vie à un cadre qui vous ressemble.
                    </p>
                    <p className="text-lg text-gray-700">

                        ISILIVING – Sublimez vos espaces avec style et élégance.
                    </p>
                </div>
            </div>
            {/* Section Équipe - Single centered photo */}
            <div className="container mx-auto py-12 md:py-24 px-4">
                {/* <h2 className="text-3xl md:text-5xl font-cormorant text-center mb-8 md:mb-16">
                    Notre Équipe
                </h2> */}
                <div className="flex justify-center">
                    {teamMembers.map((member) => (
                        <motion.div
                            key={member.name}
                            className="group relative overflow-hidden rounded-lg shadow-lg max-w-md w-full"
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="aspect-[3/4] overflow-hidden">
                                <img
                                    src={member.image}
                                    alt={member.name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6 text-cream">
                                <h3 className="text-xl md:text-2xl font-cormorant">{member.name}</h3>
                                <p className="text-cream/80 text-sm md:text-base">{member.role}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Section Contact - Responsive padding et spacing */}}
            <div className="bg-cocoa py-12 md:py-24 text-cream px-4">
                <div className="container mx-auto flex flex-col items-center">
                    <h2 className="text-3xl md:text-5xl font-cormorant text-center mb-8 md:mb-16">
                        Contactez-nous
                    </h2>
                    <p className="text-center max-w-lg mb-8 md:mb-12 text-sm md:text-base">
                        Vous avez un projet d'aménagement ou de décoration d'intérieur ?
                        Contactez-nous pour une consultation gratuite.
                    </p>
                    <a
                        href="/contact"
                        className="bg-cream text-cocoa px-6 md:px-8 py-2 md:py-3 rounded-full 
                                 font-semibold shadow-lg hover:shadow-xl transition-all duration-300
                                 text-sm md:text-base"
                    >
                        Prendre Rendez-vous
                    </a>
                </div>
            </div>
        </div>
    );
};

export default OurHistoryPage;